import React, { useState, useEffect, useContext, useMemo } from 'react'
import axios from 'axios'
import AuthContext from '../../../middlewares/AuthContext'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTableList, faTriangleExclamation, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'
import 'chart.js/auto'
import { Chart } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface DashboardSnap {
  activated : number
  renovated : number
  to_expire : number
  expired   : number
  available : number
  createdAt?: string
}

// ── Stat card ─────────────────────────────────────────────────────────────────
interface StatCardProps {
  value : number
  label : string
  sub?  : string
  icon  : any
  color : string
  delay : string
}

const StatCard: React.FC<StatCardProps> = ({ value, label, sub, icon, color, delay }) => (
  <div className="col-sm-6 col-md-3 mb-3 animate__animated animate__fadeInUp" style={{ animationDelay: delay }}>
    <div style={{
      backgroundColor : '#fff',
      borderRadius    : 16,
      boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
      border          : '1px solid #f1f5f9',
      overflow        : 'hidden',
      height          : '100%',
    }}>
      <div style={{ height: 3, backgroundColor: color }} />
      <div style={{ padding: '1.25rem 1.25rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </p>
            <p style={{ margin: 0, fontSize: '2rem', fontWeight: 900, color: '#1e293b', lineHeight: 1.1 }}>
              {value}
            </p>
            {sub && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>{sub}</p>
            )}
          </div>
          <div style={{
            width           : 44,
            height          : 44,
            borderRadius    : 12,
            backgroundColor : `${color}18`,
            display         : 'flex',
            alignItems      : 'center',
            justifyContent  : 'center',
            flexShrink      : 0,
          }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: '1.2rem' }} />
          </div>
        </div>
      </div>
    </div>
  </div>
)

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton: React.FC = () => (
  <div className="row animate__animated animate__fadeIn">
    {[0, 1, 2, 3].map(i => (
      <div key={i} className="col-sm-6 col-md-3 mb-3">
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          height          : 110,
        }}>
          <div style={{ height: 3, backgroundColor: '#e2e8f0' }} />
          <div style={{ padding: '1.25rem' }}>
            <span className="placeholder-glow d-block mb-2">
              <span className="placeholder col-5" style={{ borderRadius: 6 }} />
            </span>
            <span className="placeholder-glow d-block">
              <span className="placeholder col-3" style={{ borderRadius: 6, height: 28 }} />
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
)

// ── SubpartnerDashboard ───────────────────────────────────────────────────────
const SubpartnerDashboard: React.FC = () => {
  const { getHeaders }                  = useContext(AuthContext) as AuthCtx
  const [dashboardOne, setDashboardOne] = useState<DashboardSnap | null>(null)
  const [snapshots, setSnapshots]       = useState<DashboardSnap[]>([])
  const [today, setToday]               = useState(DateTime.now())
  const { t, i18n }                     = useTranslation()

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get<{ message: DashboardSnap; dashboards: DashboardSnap[][] }>(
          `${url}dashboards`, { headers }
        )
        setDashboardOne(res.data.message)
        setSnapshots(res.data.dashboards[0] ?? [])
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      }
    }

    fetchDashboard()

    const interval = setInterval(() => setToday(prev => prev.plus({ seconds: 1 })), 1000)
    return () => clearInterval(interval)
  }, [])

  const lineChartData = useMemo(() => {
    if (!snapshots.length) return null
    return {
      labels  : snapshots.map(e => DateTime.fromISO(e.createdAt!, { zone: 'utc' }).toFormat('dd')),
      datasets: [
        { label: t('dashboard.activated'), data: snapshots.map(e => e.activated), backgroundColor: '#6eb446', borderColor: '#3b7d3c', borderWidth: 2 },
        { label: t('dashboard.renovated'), data: snapshots.map(e => e.renovated), backgroundColor: '#0ea5e9', borderColor: '#0284c7', borderWidth: 2 },
        { label: t('dashboard.to_expire'), data: snapshots.map(e => e.to_expire), backgroundColor: '#f59e0b', borderColor: '#d97706', borderWidth: 2 },
        { label: t('dashboard.expired'),   data: snapshots.map(e => e.expired),   backgroundColor: '#ef4444', borderColor: '#dc2626', borderWidth: 2 },
        { label: t('dashboard.available'), data: snapshots.map(e => e.available), backgroundColor: '#3b82f6', borderColor: '#2563eb', borderWidth: 2 },
      ],
    }
  }, [snapshots, i18n.language])

  if (!dashboardOne) return <Skeleton />

  const total = dashboardOne.activated + dashboardOne.renovated

  return (
    <>
      <h2 className="mb-3" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#475569' }}>
        {t('dashboard.today')}: {today.setLocale(i18n.language).toFormat('yyyy-LLLL-dd HH:mm:ss')}
      </h2>

      <div className="row animate__animated animate__fadeIn">
        <StatCard
          value={total}
          label={t('dashboard.activated')}
          sub={`${dashboardOne.activated} ${t('dashboard.licenses_activated')} · ${dashboardOne.renovated} ${t('dashboard.licenses_renovated')}`}
          icon={faCheckCircle}
          color="#6eb446"
          delay="0s"
        />
        <StatCard
          value={dashboardOne.to_expire}
          label={t('dashboard.to_expire')}
          sub={t('dashboard.licenses_to_expire')}
          icon={faTriangleExclamation}
          color="#f59e0b"
          delay="0.05s"
        />
        <StatCard
          value={dashboardOne.expired}
          label={t('dashboard.expired')}
          sub={t('dashboard.licenses_expired')}
          icon={faXmarkCircle}
          color="#ef4444"
          delay="0.1s"
        />
        <StatCard
          value={dashboardOne.available}
          label={t('dashboard.available')}
          sub={t('dashboard.licenses_available')}
          icon={faTableList}
          color="#3b82f6"
          delay="0.15s"
        />
      </div>

      {lineChartData && (
        <div
          className="animate__animated animate__fadeIn"
          style={{
            backgroundColor : '#fff',
            borderRadius    : 16,
            boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
            border          : '1px solid #f1f5f9',
            padding         : '1.5rem',
            marginBottom    : '1.5rem',
            height          : 300,
          }}
        >
          <Chart
            type="line"
            data={lineChartData}
            options={{
              maintainAspectRatio : false,
              responsive          : true,
              plugins: {
                legend : { position: 'top' },
                title  : {
                  display : true,
                  text    : today.setLocale(i18n.language).toLocaleString({ month: 'long' }),
                },
              },
              elements: {
                point : { radius: 5, borderWidth: 2 },
                line  : { borderWidth: 2 },
              },
            }}
          />
        </div>
      )}
    </>
  )
}

export default SubpartnerDashboard
